import type { Path } from "react-hook-form";
import type {
  ArtworkForm,
  PlacementInfoForm,
  Side,
  PlacementFormPosition
} from "@design-lab/shared/types";

/**
 * Needed because template literals like `info.${side}.field` produce `string`,
 * not a valid Path<ArtworkForm>.
 */
const INFO_PATHS = {
  front: {
    enhancementRequestNote: "info.front.enhancementRequestNote",
    editImageManual: "info.front.editImageManual",
    scale: "info.front.scale",
    enhancementTier: "info.front.enhancementTier",
    removeBackground: "info.front.removeBackground",
    jiffyDesignServices: "info.front.jiffyDesignServices",
    llmRedrawRequestId: "info.front.llmRedrawRequestId"
  },
  back: {
    enhancementRequestNote: "info.back.enhancementRequestNote",
    editImageManual: "info.back.editImageManual",
    scale: "info.back.scale",
    enhancementTier: "info.back.enhancementTier",
    removeBackground: "info.back.removeBackground",
    jiffyDesignServices: "info.back.jiffyDesignServices",
    llmRedrawRequestId: "info.back.llmRedrawRequestId"
  }
} as const satisfies Record<Side, Record<keyof PlacementInfoForm, Path<ArtworkForm>>>;

export function infoPath<F extends keyof PlacementInfoForm>(
  side: Side,
  field: F
): Path<ArtworkForm> {
  return INFO_PATHS[side][field];
}

/**
 * Builds a type-safe path for top-level artwork placement fields.
 * e.g., artworkPlacementPath("front") returns "front" as Path<ArtworkForm>
 */
const PLACEMENT_PATHS = {
  front: "front",
  frontLeftChest: "frontLeftChest",
  back: "back"
} as const satisfies Record<PlacementFormPosition, Path<ArtworkForm>>;

export function artworkPlacementPath(placement: PlacementFormPosition): Path<ArtworkForm> {
  return PLACEMENT_PATHS[placement];
}

/**
 * Builds a type-safe path for placement size fields.
 * e.g., artworkSizePath("front") returns "front.size" as Path<ArtworkForm>
 */
const SIZE_PATHS = {
  front: "front.size",
  frontLeftChest: "frontLeftChest.size",
  back: "back.size"
} as const satisfies Record<PlacementFormPosition, Path<ArtworkForm>>;

export function artworkSizePath(placement: PlacementFormPosition): Path<ArtworkForm> {
  return SIZE_PATHS[placement];
}

export const ARTWORK_PLACEMENT_FIELDS = [
  "selectedFrontPlacement",
  "currentPlacement",
  "info"
] as const;

/**
 * Maps ARTWORK_PLACEMENT_FIELDS to valid Path<ArtworkForm>.
 */
const ARTWORK_PLACEMENT_FIELD_PATHS = {
  selectedFrontPlacement: "selectedFrontPlacement",
  currentPlacement: "currentPlacement",
  info: "info"
} as const satisfies Record<(typeof ARTWORK_PLACEMENT_FIELDS)[number], Path<ArtworkForm>>;

export function artworkPlacementFieldPath(
  field: (typeof ARTWORK_PLACEMENT_FIELDS)[number]
): Path<ArtworkForm> {
  return ARTWORK_PLACEMENT_FIELD_PATHS[field];
}
